import React, { useCallback, useState } from 'react';
import { Box, Typography, Paper, Button, Alert } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import type { GPXData } from '../../services/gpxProcessor';

interface GPXFileUploaderProps {
  onFileUploaded: (file: File, gpxData: GPXData) => void;
}

export default function GPXFileUploader({ onFileUploaded }: GPXFileUploaderProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const parseGPXFile = (file: File): Promise<GPXData> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(text, 'text/xml');
          
          // Check for parsing errors
          const parserError = xmlDoc.getElementsByTagName('parsererror')[0];
          if (parserError) {
            reject(new Error('Invalid GPX file format'));
            return;
          }
          
          // Extract basic info
          const nameElement = xmlDoc.getElementsByTagName('name')[0];
          const name = nameElement?.textContent || `Route from ${file.name.replace('.gpx', '')}`;
          
          // Extract track points
          const trackPoints = xmlDoc.getElementsByTagName('trkpt');
          const tracks = xmlDoc.getElementsByTagName('trk');
          
          if (trackPoints.length === 0) {
            reject(new Error('No track points found in GPX file'));
            return;
          }
          
          // Parse all waypoints
          const waypoints: { lat: number; lng: number }[] = [];
          let minLat = Infinity, maxLat = -Infinity;
          let minLng = Infinity, maxLng = -Infinity;
          let firstTimestamp: string | undefined;
          let lastTimestamp: string | undefined;
          
          for (let i = 0; i < trackPoints.length; i++) {
            const trkpt = trackPoints[i];
            const lat = parseFloat(trkpt.getAttribute('lat') || '0');
            const lng = parseFloat(trkpt.getAttribute('lon') || '0');
            
            if (!isNaN(lat) && !isNaN(lng)) {
              waypoints.push({ lat, lng });
              minLat = Math.min(minLat, lat);
              maxLat = Math.max(maxLat, lat);
              minLng = Math.min(minLng, lng);
              maxLng = Math.max(maxLng, lng);
              
              // Extract timestamps if available
              const timeElement = trkpt.getElementsByTagName('time')[0];
              if (timeElement?.textContent) {
                if (!firstTimestamp) firstTimestamp = timeElement.textContent;
                lastTimestamp = timeElement.textContent;
              }
            }
          }
          
          if (waypoints.length === 0) {
            reject(new Error('No valid track points found in GPX file'));
            return;
          }
          
          // Extract device info from metadata
          const metadataElement = xmlDoc.getElementsByTagName('metadata')[0];
          let deviceInfo: any = undefined;
          
          if (metadataElement) {
            const creatorAttr = xmlDoc.documentElement.getAttribute('creator');
            const linkElements = metadataElement.getElementsByTagName('link');
            
            deviceInfo = {
              creator: creatorAttr,
              links: Array.from(linkElements).map(link => ({
                href: link.getAttribute('href'),
                text: link.getElementsByTagName('text')[0]?.textContent
              }))
            };
          }
          
          const gpxData: GPXData = {
            name,
            waypoints,
            metadata: {
              filename: file.name,
              trackCount: tracks.length,
              totalPoints: trackPoints.length,
              deviceInfo,
              timestamps: firstTimestamp && lastTimestamp ? {
                start: firstTimestamp,
                end: lastTimestamp
              } : undefined,
              bounds: {
                minLat,
                maxLat,
                minLng,
                maxLng
              }
            }
          };
          
          resolve(gpxData);
          
        } catch (error) {
          console.error('GPX parsing error:', error);
          reject(new Error('Failed to parse GPX file'));
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const handleFileUpload = useCallback(async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.gpx')) {
      setUploadError('Please select a GPX file');
      return;
    }

    try {
      setUploadError(null);
      const gpxData = await parseGPXFile(file);
      onFileUploaded(file, gpxData);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Failed to process GPX file');
    }
  }, [onFileUploaded]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const gpxFile = files.find(file => file.name.toLowerCase().endsWith('.gpx'));
    
    if (gpxFile) {
      handleFileUpload(gpxFile);
    } else {
      setUploadError('Please drop a GPX file');
    }
  }, [handleFileUpload]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  }, [handleFileUpload]);

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          border: '2px dashed',
          borderColor: isDragOver ? 'primary.main' : 'grey.300',
          backgroundColor: isDragOver ? 'action.hover' : 'transparent',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover'
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        
        <Typography variant="h6" gutterBottom>
          Upload GPX File
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Drag and drop your GPX file here, or click to browse
        </Typography>

        <input
          type="file"
          accept=".gpx"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          id="gpx-file-input"
        />
        
        <label htmlFor="gpx-file-input">
          <Button
            variant="contained"
            component="span"
            startIcon={<CloudUpload />}
          >
            Choose GPX File
          </Button>
        </label>
      </Paper>

      {uploadError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {uploadError}
        </Alert>
      )}
    </Box>
  );
}
